import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { organizationService } from '../api';
import { Button, Input, ThemedText, ActionMenu, ConfirmDialog, ErrorState } from '../components';
import { extractItems } from '../utils/responseUtils';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ModalOverlay as Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { StatusBadge } from '../components/Badge';
import { SearchContainer, StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { FormGroup, FormLabel as Label } from '../components/FormStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import { Search, X } from 'lucide-react';

const OrgNameLink = styled.span`
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.default};

  &:hover {
    text-decoration: underline;
  }
`;

const InternalBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  background-color: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
  margin-left: ${props => props.theme.spacing.xs};
`;


const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
`;

const OrganizationsPage = () => {
  const theme = useTheme();
  const { isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    adminPassword: '',
  });

  useEffect(() => {
    fetchOrganizations();
  }, [includeInactive]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const response = await organizationService.getOrganizations({ includeInactive });

      setOrganizations(extractItems(response));
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      setFetchError(error?.response?.data?.message || 'Failed to load organizations. Please check your connection and try again.');
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();

    const validation = organizationService.validateCreateData(formData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    try {
      await organizationService.createOrganization(formData);
      toast.success('Organization created successfully');
      setIsCreateModalOpen(false);
      resetForm();
      fetchOrganizations();
    } catch (error) {
      console.error('Failed to create organization:', error);
      toast.error(error.response?.data?.message || 'Failed to create organization');
    }
  };

  const handleUpdateOrganization = async (e) => {
    e.preventDefault();

    // Demo configuration (isDemo/demoSlug/demoLandingJson) is owned solely by the
    // Demo Studio pages now — intentionally NOT sent here so basic org edits can't
    // wipe a demo's landing config.
    const updateData = {
      name: formData.name,
      description: formData.description,
      logo: formData.logo,
      contactEmail: formData.contactEmail,
      contactPhone: formData.contactPhone,
      address: formData.address,
      isInternalTaskOrganization: formData.isInternalTaskOrganization,
    };

    const validation = organizationService.validateUpdateData(updateData);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    try {
      await organizationService.updateOrganization(selectedOrganization.id, updateData);
      toast.success('Organization updated successfully');
      setIsEditModalOpen(false);
      resetForm();
      fetchOrganizations();
    } catch (error) {
      console.error('Failed to update organization:', error);
      toast.error(error.response?.data?.message || 'Failed to update organization');
    }
  };

  const handleToggleActive = async (organization) => {
    try {
      if (organization.isActive) {
        await organizationService.deactivateOrganization(organization.id);
        toast.success('Organization deactivated');
      } else {
        await organizationService.activateOrganization(organization.id);
        toast.success('Organization activated');
      }
      fetchOrganizations();
    } catch (error) {
      console.error('Failed to toggle organization status:', error);
      toast.error('Failed to update organization status');
    }
  };

  const handleDeleteOrganization = async (id) => {
    try {
      await organizationService.deleteOrganization(id);
      toast.success('Organization deleted successfully');
      setDeleteConfirmId(null);
      fetchOrganizations();
    } catch (error) {
      console.error('Failed to delete organization:', error);
      toast.error(error.response?.data?.message || 'Failed to delete organization');
    }
  };

  const openEditModal = (organization) => {
    setSelectedOrganization(organization);
    setFormData({
      name: organization.name || '',
      description: organization.description || '',
      logo: organization.logo || '',
      contactEmail: organization.contactEmail || '',
      contactPhone: organization.contactPhone || '',
      address: organization.address || '',
      isInternalTaskOrganization: organization.isInternalTaskOrganization || false,
      adminEmail: '',
      adminFirstName: '',
      adminLastName: '',
      adminPassword: '',
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      adminEmail: '',
      adminFirstName: '',
      adminLastName: '',
      adminPassword: '',
    });
    setSelectedOrganization(null);
  };

  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isSuperAdmin()) {
    return (
      <Layout currentPath="/organizations">
        <PageContainer theme={theme}>
          <ThemedText type="title">Access Denied</ThemedText>
          <ThemedText>You do not have permission to access this page.</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath="/organizations">
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <div>
            <ThemedText type="title">Organizations</ThemedText>
            <ThemedText type="subtitle">Manage all organizations in the system</ThemedText>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            + Create Organization
          </Button>
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>

          <ToggleSwitch theme={theme}>
            <ToggleInput
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <ToggleSlider theme={theme} $checked={includeInactive} />
            <ThemedText type="defaultSemiBold">Show inactive</ThemedText>
          </ToggleSwitch>
        </SearchContainer>

        <TableContainer theme={theme}>
          {loading ? (
            <LoadingState theme={theme}>
              <ThemedText>Loading organizations...</ThemedText>
            </LoadingState>
          ) : fetchError ? (
            <ErrorState
              theme={theme}
              title="Failed to load organizations"
              message={fetchError}
              onRetry={fetchOrganizations}
            />
          ) : filteredOrganizations.length === 0 ? (
            <EmptyState theme={theme}>
              <ThemedText>No organizations found</ThemedText>
            </EmptyState>
          ) : (
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>Name</Th>
                  <Th theme={theme}>Contact</Th>
                  <Th theme={theme}>Status</Th>
                  <Th theme={theme} style={{ width: '50px' }}></Th>
                </tr>
              </Thead>
              <Tbody>
                {filteredOrganizations.map(org => (
                  <Tr key={org.id} theme={theme} onClick={() => navigate(`/organizations/${org.id}`)}>
                    <Td theme={theme}>
                      <OrgNameLink theme={theme}>{org.name}</OrgNameLink>
                      {org.description && (
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          {org.description}
                        </ThemedText>
                      )}
                    </Td>
                    <Td theme={theme}>
                      {org.contactEmail && <ThemedText type="small">{org.contactEmail}</ThemedText>}
                      {org.contactPhone && <ThemedText type="small">{org.contactPhone}</ThemedText>}
                    </Td>
                    <Td theme={theme}>
                      <StatusBadge theme={theme} isActive={org.isActive}>
                        {org.isActive ? 'Active' : 'Inactive'}
                      </StatusBadge>
                      {org.isInternalTaskOrganization && (
                        <InternalBadge theme={theme}>Internal Tasks</InternalBadge>
                      )}
                    </Td>
                    <Td theme={theme} onClick={(e) => e.stopPropagation()}>
                      <ActionMenu actions={[
                        { label: 'Edit', onClick: () => openEditModal(org) },
                        { label: org.isActive ? 'Deactivate' : 'Activate', onClick: () => handleToggleActive(org) },
                        { label: 'Delete', onClick: () => setDeleteConfirmId(org.id), variant: 'danger' },
                      ]} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>

        {/* Create Modal */}
        <Modal theme={theme} isOpen={isCreateModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Create Organization</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsCreateModalOpen(false); resetForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleCreateOrganization}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Organization Name *</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter organization name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter description"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="contact@example.com"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Contact Phone</Label>
                  <Input
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Enter address"
                  />
                </FormGroup>

                <ThemedText type="defaultSemiBold" style={{ marginTop: theme.spacing.md }}>
                  Admin User Details
                </ThemedText>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Admin Email *</Label>
                  <Input
                    required
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    placeholder="admin@example.com"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Admin First Name *</Label>
                  <Input
                    required
                    value={formData.adminFirstName}
                    onChange={(e) => setFormData({ ...formData, adminFirstName: e.target.value })}
                    placeholder="Enter first name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Admin Last Name *</Label>
                  <Input
                    required
                    value={formData.adminLastName}
                    onChange={(e) => setFormData({ ...formData, adminLastName: e.target.value })}
                    placeholder="Enter last name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Admin Password *</Label>
                  <Input
                    required
                    type="password"
                    value={formData.adminPassword}
                    onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                  />
                </FormGroup>
              </ModalBody>

              <ModalFooter theme={theme}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setIsCreateModalOpen(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create Organization
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        {/* Edit Modal */}
        <Modal theme={theme} isOpen={isEditModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Edit Organization</ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setIsEditModalOpen(false); resetForm(); }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={handleUpdateOrganization}>
              <ModalBody theme={theme}>
                <FormGroup theme={theme}>
                  <Label theme={theme}>Organization Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter organization name"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter description"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Contact Email</Label>
                  <Input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="contact@example.com"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Contact Phone</Label>
                  <Input
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <Label theme={theme}>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Enter address"
                  />
                </FormGroup>

                <FormGroup theme={theme}>
                  <ToggleSwitch theme={theme}>
                    <ToggleInput
                      type="checkbox"
                      checked={formData.isInternalTaskOrganization}
                      onChange={(e) => setFormData({ ...formData, isInternalTaskOrganization: e.target.checked })}
                    />
                    <ToggleSlider theme={theme} $checked={formData.isInternalTaskOrganization} />
                    <Label theme={theme} style={{ cursor: 'pointer', marginBottom: 0 }}>Internal Task Organization</Label>
                  </ToggleSwitch>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Designates this organization as the internal task hub. Only one organization can be the internal task org.
                  </ThemedText>
                </FormGroup>
              </ModalBody>

              <ModalFooter theme={theme}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setIsEditModalOpen(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update Organization
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Organization"
          message="Are you sure you want to delete this organization? This action cannot be undone."
          onConfirm={() => handleDeleteOrganization(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
      </PageContainer>
    </Layout>
  );
};

export default OrganizationsPage;
export { OrganizationsPage };
